import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Alert, Box } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { FA_STORAGE_KEY, PAGE_LIMIT_OPTIONS } from "../constants";
import { updateDocument, deleteDocument } from "../services/firestoreDataService";
import { createBackups } from "../services/backupService";
import { writeAuditLog } from "../services/auditLogService";
import { trackWrite } from "../services/monitorService";
import { collectColumns } from "../utils/firestoreValueUtils";
import DocumentEditorModal from "../components/DocumentEditorModal";
import BulkOperationsPanel from "../components/BulkOperationsPanel";
import ExplorerToolbar from "./ExplorerToolbar";
import EnterpriseDataGrid from "./EnterpriseDataGrid";
import RowDetailDrawer from "./RowDetailDrawer";
import {
  WINS_ATTENDANCE_COLLECTION,
  applyFieldFilters,
  applyGlobalSearch,
  collectDistinctValues,
  collectFieldNames,
  collectFieldTypes,
} from "./explorerFilterUtils";
import {
  DEFAULT_ATTENDANCE_STATE,
  applyAttendanceFilters,
  getAttendanceField,
  isAttendanceActive,
} from "./attendanceFilterConfig";
import { listPresets, savePreset, deletePreset, loadColumnPrefs, saveColumnPrefs, clearColumnPrefs } from "./explorerPrefsService";
import { exportCsv, exportExcel, exportJson, printRows, exportPdf } from "./explorerExport";
import QueryEngineBar from "./QueryEngineBar";
import QueryExplainDialog from "./QueryExplainDialog";
import QueryHistoryDialog from "./QueryHistoryDialog";
import ExplorerSavedViewsDialog from "./ExplorerSavedViewsDialog";
import AggregateDialog from "./AggregateDialog";
import AttendanceAnalyticsDialog from "./AttendanceAnalyticsDialog";
import EmployeeTimelineDialog from "./EmployeeTimelineDialog";
import { planQuery, executeServerQuery, applyAdvancedSearch } from "../services/queryEngineService";
import { buildExplain } from "../services/queryExplainService";
import { recordQueryExecution, auditQueryExecution } from "../services/queryHistoryService";
import { getLiveMode, startIntervalRefresh, startSnapshotListener } from "../services/liveRefreshService";

const DEFAULT_COLUMN_PREFS = { order: [], hidden: {}, widths: {}, pinnedLeft: [], pinnedRight: [], density: "standard" };

/** Live dark-mode flag synced to the `fa-dark-root` class set by the layout. */
function useDarkMode() {
  const [dark, setDark] = useState(
    () =>
      (typeof document !== "undefined" && document.documentElement.classList.contains("fa-dark-root")) ||
      localStorage.getItem(FA_STORAGE_KEY) === "1"
  );
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const el = document.documentElement;
    const observer = new MutationObserver(() => setDark(el.classList.contains("fa-dark-root")));
    observer.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

export default function EnterpriseExplorer() {
  const {
    collectionName,
    allCollections,
    selectCollection,
    rows,
    selected,
    setSelected,
    loading,
    hasMore,
    loadDocuments,
    recordLimit,
    setRecordLimit,
    totalCount,
    sortField,
    sortAsc,
    user,
    can,
    Permission,
  } = useFirestoreAdmin();

  const dark = useDarkMode();
  const theme = useMemo(() => createTheme({ palette: { mode: dark ? "dark" : "light" } }), [dark]);
  const apiRef = useGridApiRef();

  const isAttendance = collectionName === WINS_ATTENDANCE_COLLECTION;

  const [globalSearch, setGlobalSearch] = useState("");
  const [filters, setFilters] = useState([]);
  const [attendance, setAttendance] = useState(DEFAULT_ATTENDANCE_STATE);
  const [columnPrefs, setColumnPrefs] = useState(DEFAULT_COLUMN_PREFS);
  const [presets, setPresets] = useState([]);
  const [drawerRow, setDrawerRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [error, setError] = useState(null);

  /* ---------------------- Sprint 11: query engine ---------------------- */
  const [serverResult, setServerResult] = useState(null); // { rows, cursor, hasMore, source }
  const [queryMode, setQueryMode] = useState("client");
  const [running, setRunning] = useState(false);
  const [advanced, setAdvanced] = useState({ field: "__all__", mode: "contains", term: "" });
  const [liveModeId, setLiveModeId] = useState("off");
  const [liveError, setLiveError] = useState(null);
  const [highlightIds, setHighlightIds] = useState(new Set());
  const [explain, setExplain] = useState(null);
  const [lastMetrics, setLastMetrics] = useState(null);
  const [missingIndex, setMissingIndex] = useState(null);
  const [explainOpen, setExplainOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [viewsOpen, setViewsOpen] = useState(false);
  const [aggregateOpen, setAggregateOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [timelineEmp, setTimelineEmp] = useState(null);
  const serverActive = Boolean(serverResult);

  // Reset filter state + load persisted prefs whenever the collection changes.
  useEffect(() => {
    setGlobalSearch("");
    setFilters([]);
    setAttendance(DEFAULT_ATTENDANCE_STATE);
    setDrawerRow(null);
    setServerResult(null);
    setQueryMode("client");
    setAdvanced({ field: "__all__", mode: "contains", term: "" });
    setHighlightIds(new Set());
    setMissingIndex(null);
    setLiveModeId("off");
    if (!collectionName) {
      setPresets([]);
      setColumnPrefs(DEFAULT_COLUMN_PREFS);
      return;
    }
    setPresets(listPresets(collectionName));
    setColumnPrefs({ ...DEFAULT_COLUMN_PREFS, ...(loadColumnPrefs(collectionName) || {}) });
  }, [collectionName]);

  const updateColumnPrefs = useCallback(
    (next) => {
      setColumnPrefs(next);
      if (collectionName) saveColumnPrefs(collectionName, next);
    },
    [collectionName]
  );

  const fieldNames = useMemo(() => collectFieldNames(rows), [rows]);
  const fieldTypes = useMemo(() => collectFieldTypes(rows), [rows]);
  const columnFields = useMemo(() => collectColumns(rows), [rows]);

  const distinctFor = useCallback((field) => collectDistinctValues(rows, field), [rows]);

  const attendanceDistinctFor = useCallback(
    (dimension) => {
      const set = new Set();
      for (const row of rows) {
        const value = getAttendanceField(row, dimension);
        if (value != null && value !== "" && typeof value !== "object") set.add(String(value));
        if (set.size >= 200) break;
      }
      return [...set].sort();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [rows]
  );

  // When a server query (or realtime stream) is active, use its documents as
  // the base set; otherwise use the provider's paginated page. All client-side
  // refinements still apply on top, so results stay identical to before when no
  // server query is running.
  const baseRows = serverActive ? serverResult.rows : rows;

  const filteredRows = useMemo(() => {
    let result = applyGlobalSearch(baseRows, globalSearch);
    result = applyFieldFilters(result, filters);
    result = applyAdvancedSearch(result, advanced);
    if (isAttendance && isAttendanceActive(attendance)) {
      result = applyAttendanceFilters(result, attendance);
    }
    return result;
  }, [baseRows, globalSearch, filters, advanced, isAttendance, attendance]);

  const exportColumns = useMemo(() => {
    const base = (columnPrefs.order || []).filter((f) => columnFields.includes(f));
    const missing = columnFields.filter((f) => !base.includes(f));
    return [...base, ...missing].filter((f) => !columnPrefs.hidden?.[f]);
  }, [columnFields, columnPrefs]);

  /* ----------------------------- actions ----------------------------- */

  /** Execute (or paginate) a server-side Firestore query for the current filters. */
  const runServerQuery = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      const t0 = performance.now();
      const result = await executeServerQuery({
        collectionName,
        filters,
        sortField,
        sortAsc,
        pageLimit: recordLimit,
        cursor,
      });
      const totalTimeMs = Math.round(performance.now() - t0);

      if (!result.ok) {
        setMissingIndex(result.missingIndex || null);
        setExplain(
          buildExplain({
            plan: result.plan,
            metrics: result.metrics,
            timings: { totalTimeMs },
            error: result.error,
            missingIndex: result.missingIndex,
          })
        );
        setError(result.error?.message || "Server query failed");
        return result;
      }

      setMissingIndex(null);
      setError(null);
      setServerResult((prev) => ({
        rows: append && prev ? [...prev.rows, ...result.rows] : result.rows,
        cursor: result.cursor,
        hasMore: result.hasMore,
        source: "query",
      }));
      setQueryMode("server");
      setLastMetrics(result.metrics);
      setExplain(
        buildExplain({
          plan: result.plan,
          metrics: result.metrics,
          timings: { queryTimeMs: result.metrics.queryTimeMs, networkTimeMs: result.metrics.queryTimeMs, totalTimeMs },
        })
      );
      return result;
    },
    [collectionName, filters, sortField, sortAsc, recordLimit]
  );

  const runRefresh = useCallback(async () => {
    setError(null);
    try {
      if (serverActive && serverResult.source === "query") {
        await runServerQuery({ cursor: null, append: false });
      } else if (!serverActive) {
        await loadDocuments();
      }
      // realtime source refreshes itself via the snapshot listener
    } catch (err) {
      setError(err.message || "Failed to load documents");
    }
  }, [serverActive, serverResult, runServerQuery, loadDocuments]);

  const runLoadMore = useCallback(async () => {
    setError(null);
    try {
      if (serverActive && serverResult.source === "query" && serverResult.hasMore) {
        await runServerQuery({ cursor: serverResult.cursor, append: true });
      } else if (!serverActive) {
        await loadDocuments({ append: true });
      }
    } catch (err) {
      setError(err.message || "Failed to load more documents");
    }
  }, [serverActive, serverResult, runServerQuery, loadDocuments]);

  /** Smart planner: choose server vs client execution, audit every run. */
  const runSmartQuery = useCallback(async () => {
    if (!collectionName) return;
    setRunning(true);
    const plan = planQuery({ collectionName, filters, sortField, sortAsc, pageLimit: recordLimit });
    try {
      let result = null;
      if (plan.mode === "server") {
        result = await runServerQuery({ cursor: null, append: false });
      } else {
        setServerResult(null);
        setQueryMode("client");
        setMissingIndex(null);
        await loadDocuments();
        setExplain(buildExplain({ plan, metrics: { docsReturned: rows.length } }));
      }

      const execMs = result?.metrics?.queryTimeMs ?? 0;
      const resultCount = result?.ok ? result.rows.length : filteredRows.length;
      const advancedSearch = advanced.term ? advanced : null;
      recordQueryExecution({ collection: collectionName, filters, advancedSearch, mode: plan.mode, executionTimeMs: execMs, resultCount });
      auditQueryExecution({
        user: user?.email,
        userUid: user?.uid,
        collection: collectionName,
        filters,
        advancedSearch,
        mode: plan.mode,
        executionTimeMs: execMs,
        resultCount,
      });

      if (!result || result.ok) toast.success(plan.mode === "server" ? "Server query executed" : "Client query applied");
    } catch (err) {
      setError(err.message || "Query failed");
    } finally {
      setRunning(false);
    }
  }, [collectionName, filters, sortField, sortAsc, recordLimit, runServerQuery, loadDocuments, rows.length, filteredRows.length, advanced, user]);

  const clearServerMode = useCallback(() => {
    setServerResult(null);
    setQueryMode("client");
    setMissingIndex(null);
    setLiveModeId("off");
  }, []);

  const handleLiveModeChange = useCallback((id) => {
    setLiveModeId(id);
    if (getLiveMode(id).id === "off") {
      setServerResult((prev) => (prev && prev.source === "realtime" ? null : prev));
    }
  }, []);

  // Keep a stable reference to the latest refresh fn so the live-refresh effect
  // does not re-subscribe on every state change (important for realtime mode).
  const runRefreshRef = useRef(runRefresh);
  useEffect(() => {
    runRefreshRef.current = runRefresh;
  }, [runRefresh]);

  // Live refresh: interval polling or a realtime snapshot listener.
  useEffect(() => {
    setLiveError(null);
    const mode = getLiveMode(liveModeId);
    if (mode.id === "off" || !collectionName) return undefined;
    if (mode.realtime) {
      const unsub = startSnapshotListener({
        collectionName,
        sortField,
        sortAsc,
        pageLimit: recordLimit,
        onData: ({ rows: liveRows }) =>
          setServerResult({ rows: liveRows, cursor: null, hasMore: false, source: "realtime" }),
        onError: (e) => setLiveError(e.message || String(e)),
      });
      return () => unsub && unsub();
    }
    const stop = startIntervalRefresh(mode.intervalMs, () => runRefreshRef.current());
    return () => stop && stop();
  }, [liveModeId, collectionName, sortField, sortAsc, recordLimit]);

  const applySavedView = useCallback(
    (view) => {
      const st = view.state || {};
      setGlobalSearch(st.globalSearch || "");
      setFilters(Array.isArray(st.filters) ? st.filters : []);
      setAttendance({ ...DEFAULT_ATTENDANCE_STATE, ...(st.attendance || {}) });
      if (st.columnPrefs) updateColumnPrefs({ ...DEFAULT_COLUMN_PREFS, ...st.columnPrefs });
      if (st.pageSize) setRecordLimit(st.pageSize);
      toast.success(`Applied view "${view.name}"`);
    },
    [updateColumnPrefs, setRecordLimit]
  );

  const runQueryAgain = useCallback((entry) => {
    setHistoryOpen(false);
    setFilters(
      Array.isArray(entry.filters)
        ? entry.filters.map((f, i) => ({ id: `h_${i}_${Date.now()}`, type: "string", ...f }))
        : []
    );
    if (entry.advancedSearch) {
      setAdvanced({
        field: entry.advancedSearch.field || "__all__",
        mode: entry.advancedSearch.mode || "contains",
        term: entry.advancedSearch.term || "",
      });
    }
    toast.success('Query loaded — press "Run Smart Query"');
  }, []);

  const currentViewState = useMemo(
    () => ({
      globalSearch,
      filters,
      attendance,
      columnPrefs,
      sortField,
      sortAsc,
      pageSize: recordLimit,
      density: columnPrefs.density,
    }),
    [globalSearch, filters, attendance, columnPrefs, sortField, sortAsc, recordLimit]
  );

  const resetFilters = useCallback(() => {
    setGlobalSearch("");
    setFilters([]);
    setAttendance(DEFAULT_ATTENDANCE_STATE);
  }, []);

  const handleSavePreset = useCallback(
    (name) => {
      if (!collectionName) return;
      savePreset(collectionName, name, { globalSearch, filters, attendance });
      setPresets(listPresets(collectionName));
      toast.success(`Saved filter "${name}"`);
    },
    [collectionName, globalSearch, filters, attendance]
  );

  const handleLoadPreset = useCallback((preset) => {
    const state = preset.state || {};
    setGlobalSearch(state.globalSearch || "");
    setFilters(Array.isArray(state.filters) ? state.filters : []);
    setAttendance({ ...DEFAULT_ATTENDANCE_STATE, ...(state.attendance || {}) });
    toast.success(`Applied "${preset.name}"`);
  }, []);

  const handleDeletePreset = useCallback(
    (id) => {
      if (!collectionName) return;
      deletePreset(collectionName, id);
      setPresets(listPresets(collectionName));
    },
    [collectionName]
  );

  const handleExport = useCallback(
    (type) => {
      if (!can(Permission.export) && type !== "print") {
        toast.error("You do not have export permission");
        return;
      }
      const prefix = `${collectionName || "collection"}`.replace(/[^\w-]+/g, "_");
      if (type === "excel") exportExcel(filteredRows, exportColumns, prefix);
      else if (type === "csv") exportCsv(filteredRows, exportColumns, prefix);
      else if (type === "json") exportJson(filteredRows, prefix);
      else if (type === "print") printRows(filteredRows, exportColumns, `${collectionName} export`);
      else if (type === "pdf") {
        exportPdf(filteredRows, exportColumns, prefix, `${collectionName} export`).catch((err) =>
          toast.error(`PDF export failed: ${err.message}`)
        );
      }
    },
    [can, Permission.export, filteredRows, exportColumns, collectionName]
  );

  const handleAutoWidth = useCallback(() => {
    apiRef.current?.autosizeColumns?.({ includeHeaders: true, includeOutliers: true });
  }, [apiRef]);

  const handleResetColumns = useCallback(() => {
    if (collectionName) clearColumnPrefs(collectionName);
    setColumnPrefs(DEFAULT_COLUMN_PREFS);
  }, [collectionName]);

  const onSaveDocument = useCallback(
    async (docId, newData, oldData) => {
      await createBackups({
        collectionName,
        documents: { [docId]: oldData },
        adminUid: user?.uid,
        adminName: user?.email,
        operation: "update_single",
      });
      await updateDocument(collectionName, docId, newData, user?.uid || user?.email);
      trackWrite(1);
      await writeAuditLog({
        adminName: user?.email,
        adminUid: user?.uid,
        action: "update_document",
        collection: collectionName,
        documentIds: [docId],
        oldValues: oldData,
        newValues: newData,
      });
      toast.success("Document saved");
      await loadDocuments();
    },
    [collectionName, user, loadDocuments]
  );

  const handleDelete = useCallback(
    async (row) => {
      if (!row) return;
      try {
        await createBackups({
          collectionName,
          documents: { [row.id]: row.data },
          adminUid: user?.uid,
          adminName: user?.email,
          operation: "delete_single",
        });
        await deleteDocument(collectionName, row.id, user?.uid || user?.email);
        trackWrite(1);
        await writeAuditLog({
          adminName: user?.email,
          adminUid: user?.uid,
          action: "delete_document",
          collection: collectionName,
          documentIds: [row.id],
          oldValues: row.data,
          newValues: null,
        });
        toast.success("Document deleted (backup created)");
        setDrawerRow(null);
        await loadDocuments();
      } catch (err) {
        toast.error(`Delete failed: ${err.message}`);
      }
    },
    [collectionName, user, loadDocuments]
  );

  /* --------------------------- drawer nav ---------------------------- */

  const drawerIndex = useMemo(
    () => (drawerRow ? filteredRows.findIndex((r) => r.id === drawerRow.id) : -1),
    [drawerRow, filteredRows]
  );

  const gotoDrawer = useCallback(
    (delta) => {
      if (drawerIndex < 0) return;
      const next = filteredRows[drawerIndex + delta];
      if (next) setDrawerRow(next);
    },
    [drawerIndex, filteredRows]
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <ExplorerToolbar
          collectionName={collectionName}
          allCollections={allCollections}
          onSelectCollection={selectCollection}
          isAttendance={isAttendance}
          globalSearch={globalSearch}
          onGlobalSearchChange={setGlobalSearch}
          filters={filters}
          fieldNames={fieldNames}
          fieldTypes={fieldTypes}
          distinctFor={distinctFor}
          attendanceDistinctFor={attendanceDistinctFor}
          onFiltersChange={setFilters}
          attendance={attendance}
          onAttendanceChange={setAttendance}
          presets={presets}
          onSavePreset={handleSavePreset}
          onLoadPreset={handleLoadPreset}
          onDeletePreset={handleDeletePreset}
          onReset={resetFilters}
          onRefresh={runRefresh}
          loading={loading}
          counts={{ loaded: baseRows.length, filtered: filteredRows.length, total: totalCount }}
          pageSize={recordLimit}
          pageSizeOptions={PAGE_LIMIT_OPTIONS}
          onPageSizeChange={setRecordLimit}
          hasMore={serverActive ? serverResult.hasMore : hasMore}
          onLoadMore={runLoadMore}
          columnFields={columnFields}
          columnPrefs={columnPrefs}
          onColumnPrefsChange={updateColumnPrefs}
          onAutoWidth={handleAutoWidth}
          onResetColumns={handleResetColumns}
          onExport={handleExport}
        />

        <QueryEngineBar
          mode={queryMode}
          serverActive={serverActive && serverResult.source === "query"}
          running={running}
          onRunQuery={runSmartQuery}
          onClearServer={clearServerMode}
          liveModeId={liveModeId}
          onLiveModeChange={handleLiveModeChange}
          liveError={liveError}
          advanced={advanced}
          onAdvancedChange={setAdvanced}
          fieldNames={fieldNames}
          isAttendance={isAttendance}
          onOpenExplain={() => setExplainOpen(true)}
          onOpenHistory={() => setHistoryOpen(true)}
          onOpenViews={() => setViewsOpen(true)}
          onOpenAggregate={() => setAggregateOpen(true)}
          onOpenAnalytics={() => setAnalyticsOpen(true)}
          missingIndex={missingIndex}
          metrics={lastMetrics}
        />

        <BulkOperationsPanel onDone={loadDocuments} />

        <EnterpriseDataGrid
          apiRef={apiRef}
          rows={filteredRows}
          fields={columnFields}
          fieldTypes={fieldTypes}
          prefs={columnPrefs}
          onPrefsChange={updateColumnPrefs}
          searchTerm={globalSearch}
          selectedIds={selected}
          onSelectionChange={setSelected}
          onRowOpen={setDrawerRow}
          onRowEdit={setEditRow}
          loading={loading}
          highlightIds={highlightIds}
        />
      </Box>

      <RowDetailDrawer
        open={Boolean(drawerRow)}
        row={drawerRow}
        collectionName={collectionName}
        canEdit={can(Permission.edit)}
        canDelete={can(Permission.delete)}
        onClose={() => setDrawerRow(null)}
        onEdit={(row) => {
          setEditRow(row);
        }}
        onDelete={handleDelete}
        onRestored={loadDocuments}
        onPrev={() => gotoDrawer(-1)}
        onNext={() => gotoDrawer(1)}
        hasPrev={drawerIndex > 0}
        hasNext={drawerIndex >= 0 && drawerIndex < filteredRows.length - 1}
      />

      {editRow && (
        <DocumentEditorModal
          row={editRow}
          collectionName={collectionName}
          onClose={() => setEditRow(null)}
          onSave={onSaveDocument}
        />
      )}

      <QueryExplainDialog open={explainOpen} explain={explain} onClose={() => setExplainOpen(false)} />

      <QueryHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} onRunAgain={runQueryAgain} />

      <ExplorerSavedViewsDialog
        open={viewsOpen}
        onClose={() => setViewsOpen(false)}
        collectionName={collectionName}
        currentState={currentViewState}
        onApply={applySavedView}
      />

      <AggregateDialog open={aggregateOpen} onClose={() => setAggregateOpen(false)} rows={filteredRows} fieldNames={fieldNames} />

      {isAttendance && (
        <AttendanceAnalyticsDialog
          open={analyticsOpen}
          onClose={() => setAnalyticsOpen(false)}
          rows={filteredRows}
          onHighlight={setHighlightIds}
          onOpenEmployee={(emp) => setTimelineEmp(emp)}
        />
      )}

      <EmployeeTimelineDialog
        open={Boolean(timelineEmp)}
        onClose={() => setTimelineEmp(null)}
        rows={serverActive ? serverResult.rows : rows}
        employeeId={timelineEmp}
      />
    </ThemeProvider>
  );
}
